import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import './ResizableImage.css';

/**
 * React component for rendering a resizable, alignable image inside the editor.
 */
const ResizableImageView = ({ node, updateAttributes, selected }) => {
  const { src, alt, title, width, alignment } = node.attrs;
  const [isResizing, setIsResizing] = useState(false);
  const [currentWidth, setCurrentWidth] = useState(width);
  const imgRef = useRef(null);
  const startX = useRef(0);
  const startWidth = useRef(0);

  useEffect(() => {
    setCurrentWidth(width);
  }, [width]);

  const handleMouseDown = useCallback((e, corner) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    startX.current = e.clientX;
    startWidth.current = imgRef.current?.offsetWidth || currentWidth || 300;

    const handleMouseMove = (moveEvent) => {
      const isLeft = corner === 'top-left' || corner === 'bottom-left';
      const diff = moveEvent.clientX - startX.current;
      const delta = isLeft ? -diff : diff;
      const newWidth = Math.max(50, Math.min(startWidth.current + delta, 1200));
      setCurrentWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      // Persist the width
      const finalWidth = imgRef.current?.offsetWidth || currentWidth;
      updateAttributes({ width: finalWidth });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [currentWidth, updateAttributes]);

  const handleAlignChange = useCallback((newAlignment) => {
    updateAttributes({ alignment: newAlignment });
  }, [updateAttributes]);

  const imgStyle = {};
  if (currentWidth) {
    imgStyle.width = `${currentWidth}px`;
    imgStyle.maxWidth = '100%';
  } else {
    imgStyle.maxWidth = '100%';
  }
  imgStyle.height = 'auto';

  let wrapperStyle = {};
  if (alignment === 'left') {
    wrapperStyle.textAlign = 'left';
  } else if (alignment === 'right') {
    wrapperStyle.textAlign = 'right';
  } else {
    wrapperStyle.textAlign = 'center';
  }

  return (
    <NodeViewWrapper
      className={`resizable-image-wrapper ${selected ? 'selected' : ''} ${isResizing ? 'resizing' : ''}`}
      style={wrapperStyle}
      data-drag-handle
    >
      <div className="resizable-image-inner" style={{ display: 'inline-block', position: 'relative' }}>
        <img
          ref={imgRef}
          src={src}
          alt={alt || ''}
          title={title || ''}
          style={imgStyle}
          draggable={false}
          className="resizable-image"
        />

        {selected && (
          <>
            {/* Alignment buttons */}
            <div className="image-alignment-buttons" contentEditable={false}>
              <button
                type="button"
                className={`align-btn ${alignment === 'left' ? 'active' : ''}`}
                onClick={() => handleAlignChange('left')}
                title="Align left"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="15" y2="12" /><line x1="3" y1="18" x2="18" y2="18" />
                </svg>
              </button>
              <button
                type="button"
                className={`align-btn ${alignment === 'center' || !alignment ? 'active' : ''}`}
                onClick={() => handleAlignChange('center')}
                title="Center"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="6" x2="21" y2="6" /><line x1="6" y1="12" x2="18" y2="12" /><line x1="4" y1="18" x2="20" y2="18" />
                </svg>
              </button>
              <button
                type="button"
                className={`align-btn ${alignment === 'right' ? 'active' : ''}`}
                onClick={() => handleAlignChange('right')}
                title="Align right"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="6" x2="21" y2="6" /><line x1="9" y1="12" x2="21" y2="12" /><line x1="6" y1="18" x2="21" y2="18" />
                </svg>
              </button>
            </div>

            {/* Resize handles */}
            <div
              className="resize-handle top-left"
              onMouseDown={(e) => handleMouseDown(e, 'top-left')}
            />
            <div
              className="resize-handle top-right"
              onMouseDown={(e) => handleMouseDown(e, 'top-right')}
            />
            <div
              className="resize-handle bottom-left"
              onMouseDown={(e) => handleMouseDown(e, 'bottom-left')}
            />
            <div
              className="resize-handle bottom-right"
              onMouseDown={(e) => handleMouseDown(e, 'bottom-right')}
            />
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};

/**
 * Custom TipTap Image node with resize and alignment support.
 * Backward-compatible with existing <img> tags from Quill content.
 */
const ResizableImage = Node.create({
  name: 'resizableImage',

  group: 'block',

  atom: true,

  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      title: { default: null },
      width: { default: null },
      alignment: { default: 'center' },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
        getAttrs: (dom) => {
          const width = dom.getAttribute('width') || dom.style.width?.replace('px', '') || null;
          const alignment = dom.getAttribute('data-alignment') || null;

          // Try to detect alignment from style
          let detectedAlignment = alignment;
          if (!detectedAlignment) {
            const style = dom.getAttribute('style') || '';
            if (style.includes('margin: 0px auto') || style.includes('margin:0 auto') || style.includes('margin: 0 auto')) {
              detectedAlignment = 'center';
            } else if (style.includes('float: right') || style.includes('float:right')) {
              detectedAlignment = 'right';
            } else if (style.includes('float: left') || style.includes('float:left')) {
              detectedAlignment = 'left';
            }
          }

          return {
            src: dom.getAttribute('src'),
            alt: dom.getAttribute('alt'),
            title: dom.getAttribute('title'),
            width: width ? parseInt(width, 10) : null,
            alignment: detectedAlignment || 'center',
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { alignment, width, ...rest } = HTMLAttributes;
    const style = [];

    if (width) {
      style.push(`width: ${width}px`);
      style.push('max-width: 100%');
    } else {
      style.push('max-width: 100%');
    }
    style.push('height: auto');

    if (alignment === 'center') {
      style.push('display: block');
      style.push('margin: 0 auto');
    } else if (alignment === 'right') {
      style.push('display: block');
      style.push('margin-left: auto');
      style.push('margin-right: 0');
    }

    return ['img', mergeAttributes(rest, {
      style: style.join('; '),
      'data-alignment': alignment || 'center',
      width: width || undefined,
    })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },
});

export default ResizableImage;
